/**
 * Enhanced central utilities for handling route exclusions in middlewares
 * Improved version with better performance, DRY principles, and error handling
 */

export interface RouteExclusionConfig {
  /** API routes patterns to exclude */
  apiRoutes?: readonly string[];
  /** Auth routes patterns to exclude */
  authRoutes?: readonly string[];
  /** Static asset patterns to exclude */
  staticAssets?: readonly string[];
  /** System routes patterns to exclude */
  systemRoutes?: readonly string[];
  /** Additional custom patterns to exclude */
  customPatterns?: readonly string[];
}

/**
 * Base patterns used across all environments
 */
const BASE_PATTERNS = {
  apiRoutes: ['/api/', '/api'] as const,
  authRoutes: ['/auth/', '/auth'] as const,
  systemRoutes: ['/', '/payment/return/', '/payment/return'] as const
} as const;

/**
 * Static asset patterns (without leading slash for path.replace logic)
 */
const STATIC_ASSET_PATTERNS = [
  'lighting-advisor',
  'assets',
  'fonts',
  'images',
  'videos',
  'favicon',
  '_nuxt',
  '.well-known'
] as const;

/**
 * Client-specific system routes
 */
const CLIENT_SYSTEM_ROUTES = [
  '/metrics',
  '/health',
  '/__nuxt',
  '/_nuxt/'
] as const;

/**
 * Server-specific system routes
 */
const SERVER_SYSTEM_ROUTES = ['/metrics', '/health', '/__nuxt'] as const;

/**
 * Default route exclusion patterns for client-side middlewares
 */
export const DEFAULT_EXCLUSION_PATTERNS: RouteExclusionConfig = {
  ...BASE_PATTERNS,
  staticAssets: STATIC_ASSET_PATTERNS,
  systemRoutes: [...BASE_PATTERNS.systemRoutes, ...CLIENT_SYSTEM_ROUTES]
} as const;

/**
 * Server-side specific exclusion patterns
 */
export const SERVER_EXCLUSION_PATTERNS: RouteExclusionConfig = {
  ...BASE_PATTERNS,
  staticAssets: STATIC_ASSET_PATTERNS,
  systemRoutes: [...BASE_PATTERNS.systemRoutes, ...SERVER_SYSTEM_ROUTES]
} as const;

// Performance optimization: pre-compile regex
const FILE_EXTENSION_REGEX = /\.[a-zA-Z0-9]+$/;
const WELL_KNOWN_REGEX = /\/\.well-known(?:\/|$)/;

// Memoization cache for expensive checks
const exclusionCache = new Map<string, boolean>();
const CACHE_MAX_SIZE = 1000; // Prevent memory leaks

/**
 * Input validation helper
 */
function validatePath(path: unknown): path is string {
  return typeof path === 'string' && path.length > 0;
}

/** h3 `event.path` may include `?query` — strip so extension / exact matches work. */
export function pathnameOnly(path: string): string {
  const queryIndex = path.indexOf('?');
  return queryIndex === -1 ? path : path.slice(0, queryIndex);
}

/**
 * Clear cache when it gets too large (memory management)
 */
function manageCacheSize(): void {
  if (exclusionCache.size > CACHE_MAX_SIZE) {
    // Keep only the most recent half of entries
    const entries = Array.from(exclusionCache.entries());
    exclusionCache.clear();
    entries.slice(-Math.floor(CACHE_MAX_SIZE / 2)).forEach(([key, value]) => {
      exclusionCache.set(key, value);
    });
  }
}

/**
 * Optimized pattern matching with early returns
 */
function matchesPatterns(
  path: string,
  patterns: readonly string[] = []
): boolean {
  for (const pattern of patterns) {
    if (path.startsWith(pattern)) {
      return true;
    }
  }
  return false;
}

/**
 * Optimized static asset check
 */
function matchesStaticAssets(
  path: string,
  patterns: readonly string[] = []
): boolean {
  const pathWithoutLeadingSlash = path.replace(/^\//, '');
  for (const pattern of patterns) {
    if (pathWithoutLeadingSlash.startsWith(pattern)) {
      return true;
    }
  }
  return false;
}

/**
 * Core exclusion logic with performance optimizations
 * @param path - The path to check
 * @param config - Configuration object with exclusion patterns
 * @returns true if the path should be excluded
 */
export function shouldExcludeRoute(
  path: unknown,
  config: RouteExclusionConfig = DEFAULT_EXCLUSION_PATTERNS
): boolean {
  // Input validation
  if (!validatePath(path)) {
    console.warn('shouldExcludeRoute: Invalid path provided:', path);
    return false;
  }

  // Check cache first
  const cacheKey = `${path}:${JSON.stringify(config)}`;
  if (exclusionCache.has(cacheKey)) {
    return exclusionCache.get(cacheKey)!;
  }

  let result = false;

  try {
    // Early returns for performance - check most common cases first

    // Check exact system route matches (fastest)
    if (config.systemRoutes?.includes(path)) {
      result = true;
    }
    // Check API routes (common)
    else if (config.apiRoutes && matchesPatterns(path, config.apiRoutes)) {
      result = true;
    }
    // Check auth routes (common)
    else if (config.authRoutes && matchesPatterns(path, config.authRoutes)) {
      result = true;
    }
    // Check static assets (less common)
    else if (
      config.staticAssets &&
      matchesStaticAssets(path, config.staticAssets)
    ) {
      result = true;
    }
    // Check custom patterns (least common)
    else if (
      config.customPatterns &&
      matchesPatterns(path, config.customPatterns)
    ) {
      result = true;
    }
  } catch (error) {
    console.error('Error in shouldExcludeRoute:', error, { path, config });
    result = false; // Fail safely - don't exclude on error
  }

  // Cache result with memory management
  exclusionCache.set(cacheKey, result);
  manageCacheSize();

  return result;
}

/**
 * Checks if a path has a file extension (optimized with pre-compiled regex)
 * @param path - The path to check
 * @returns true if the path has a file extension
 */
export function hasFileExtension(path: unknown): boolean {
  if (!validatePath(path)) {
    return false;
  }

  return FILE_EXTENSION_REGEX.test(path);
}

/**
 * Specific exclusion check for client-side middlewares (optimized)
 * @param path - The path to check
 * @returns true if the path should be excluded
 */
export function shouldExcludeClientRoute(path: unknown): boolean {
  if (!validatePath(path)) {
    return false;
  }

  return (
    shouldExcludeRoute(path, DEFAULT_EXCLUSION_PATTERNS) ||
    WELL_KNOWN_REGEX.test(path)
  );
}

/**
 * Specific exclusion check for server-side middlewares
 * @param path - The path to check
 * @returns true if the path should be excluded
 */
export function shouldExcludeServerRoute(path: unknown): boolean {
  return shouldExcludeRoute(path, SERVER_EXCLUSION_PATTERNS);
}

/**
 * Combined check for common middleware exclusions (main entry point)
 * @param path - The path to check
 * @param isServer - Whether this is a server-side check
 * @returns true if the path should be excluded
 */
export function shouldSkipMiddleware(path: unknown, isServer = false): boolean {
  if (!validatePath(path)) {
    return false; // Don't skip on invalid input
  }

  const pathname = pathnameOnly(path);

  if (isServer) {
    // Static asset roots only — no blanket file-extension skip. Contentful page
    // redirects may have legacy source paths like `/old-page.html`, and those must
    // still reach `30.pageRedirect`.
    return shouldExcludeServerRoute(pathname);
  }

  return shouldExcludeClientRoute(pathname) || hasFileExtension(pathname);
}

/**
 * Utility to clear the exclusion cache (useful for testing or memory management)
 */
export function clearExclusionCache(): void {
  exclusionCache.clear();
}

/**
 * Get cache statistics (useful for debugging and monitoring)
 */
export function getCacheStats(): { size: number; maxSize: number } {
  return {
    size: exclusionCache.size,
    maxSize: CACHE_MAX_SIZE
  };
}

/**
 * Create a custom exclusion checker with specific config
 * Useful for specialized middleware that needs different patterns
 */
export function createExclusionChecker(config: RouteExclusionConfig) {
  return (path: unknown): boolean => shouldExcludeRoute(path, config);
}

// Type guards for better TypeScript integration
export function isExcludedRoute(path: unknown): path is string {
  return validatePath(path) && shouldSkipMiddleware(path);
}

export function isValidRoute(path: unknown): path is string {
  return validatePath(path) && !shouldSkipMiddleware(path);
}
